/**
 * Calculate Missing Fish Intelligence from comparison data
 *
 * Extracted from MissingFishIntelligence.jsx to decouple heavy analytical
 * logic from the React rendering component.
 *
 * @param {Array} comparisons - Array of comparison objects with actual_weight_g, model_weight_g, sample_date, days_since_arrival, sample_number
 * @param {Array} dailyFeedData - Optional array of daily feed data with day and feed_kg
 * @param {Set}   excludedIndices - Indices of user-excluded samples
 * @param {number} modelScalingFactor - Active model baseline scaling (1 = raw, >1 = adjusted up)
 * @returns {Object|null} Intelligence results
 */

import {
  classifyDeviationPattern,
  calculatePositiveStreak,
  calculateDeviationStability,
  detectEarliestDivergence,
  calculateGrowthSignalStrength,
  estimateBiomassDensity,
  evaluateSampleReliability,
  findStrongestDeviationWindow,
  calculateAnalyticalStrength,
  detectDataQualityWarnings,
} from '@/utils/stockIntelligence';
import { detectIncorrectSamples } from '@/utils/missingFishDetection';

export const calculateMissingFishIntelligence = (comparisons, dailyFeedData = null, excludedIndices = new Set(), modelScalingFactor = 1, species = null, bypassSpeciesClamp = false) => {
  if (!comparisons || comparisons.length === 0) {
    return null;
  }

  // 0) Enforce strict chronological order — all analysis depends on date sequence
  const sortedComparisons = [...comparisons].sort(
    (a, b) => a.days_since_arrival - b.days_since_arrival
  );

  // 1) Compute per-sample deviation against the ACTIVE model baseline
  //    When modelScalingFactor !== 1, all model weights are scaled before comparison.
  //    This ensures the deviation table, trend classification, and confidence scoring
  //    all use the same baseline as the chart's adjusted model line and jump detection.
  const k = modelScalingFactor || 1;
  const deviations = sortedComparisons.map(comp => {
    const actualWeight = parseFloat(comp.actual_weight_g);
    const rawModel = parseFloat(comp.model_weight_g);
    const modelWeight = rawModel * k;  // Apply active baseline scaling
    const deviationPercent = ((actualWeight - modelWeight) / modelWeight) * 100;
    const roundedDeviation = Math.round(deviationPercent * 100) / 100;
    
    let tag = 'Within margin';
    let tagClass = 'bg-green-100 text-green-700';
    if (deviationPercent > 5) {
      tag = 'Above model';
      tagClass = 'bg-amber-100 text-amber-700';
    } else if (deviationPercent < -5) {
      tag = 'Below model';
      tagClass = 'bg-blue-100 text-blue-700';
    }
    
    return {
      sample_number: comp.sample_number,
      sample_date: comp.sample_date,
      days_since_arrival: comp.days_since_arrival,
      actual_weight_g: actualWeight,
      model_weight_g: modelWeight,
      deviation_percent: roundedDeviation,
      tag,
      tagClass
    };
  });

  // 1b) Suspect sample classification is now handled exclusively by the jump
  //     validation pipeline (detectConfirmedSampleJump). Significant upward
  //     deviations are first classified as potential jump events, then validated
  //     using subsequent sample confirmation.  Unconfirmed jumps are labelled
  //     "Unconfirmed jump (likely sampling error)".
  //     jumpValidatedSuspects (Set of comparison indices) is supplied by the
  //     caller after running jump analysis — never computed independently here.

  // 1c) Build filtered deviations (excluding user-excluded samples)
  const activeDeviations = deviations.filter((_, idx) => !excludedIndices.has(idx));
  const hasExclusions = excludedIndices.size > 0;

  // 2) Trend Classification — computed from ACTIVE deviations only
  const aboveCount = activeDeviations.filter(d => d.deviation_percent > 5).length;
  const belowCount = activeDeviations.filter(d => d.deviation_percent < -5).length;
  const withinCount = activeDeviations.filter(d => Math.abs(d.deviation_percent) <= 5).length;
  const totalSamples = activeDeviations.length;
  
  // Check for consecutive patterns — using ACTIVE deviations only
  let consecutiveAbove = 0;
  let consecutiveBelow = 0;
  let maxConsecutiveAbove = 0;
  let maxConsecutiveBelow = 0;
  let signFlips = 0;
  let lastSign = null;
  
  activeDeviations.forEach(d => {
    const sign = d.deviation_percent > 5 ? 'above' : d.deviation_percent < -5 ? 'below' : 'within';
    
    if (sign === 'above') {
      consecutiveAbove++;
      consecutiveBelow = 0;
      maxConsecutiveAbove = Math.max(maxConsecutiveAbove, consecutiveAbove);
    } else if (sign === 'below') {
      consecutiveBelow++;
      consecutiveAbove = 0;
      maxConsecutiveBelow = Math.max(maxConsecutiveBelow, consecutiveBelow);
    } else {
      consecutiveAbove = 0;
      consecutiveBelow = 0;
    }
    
    if (lastSign && lastSign !== sign && sign !== 'within' && lastSign !== 'within') {
      signFlips++;
    }
    if (sign !== 'within') lastSign = sign;
  });

  // Calculate variance of active deviations
  const devValues = activeDeviations.map(d => d.deviation_percent);
  const mean = devValues.reduce((a, b) => a + b, 0) / devValues.length;
  const variance = devValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / devValues.length;
  const stdDev = Math.sqrt(variance);

  let trendClassification = '';
  let trendClass = '';
  
  if (withinCount >= totalSamples * 0.7) {
    trendClassification = 'In line with model';
    trendClass = 'bg-green-100 text-green-800 border-green-300';
  } else if (maxConsecutiveAbove >= 2 && aboveCount > totalSamples / 2) {
    trendClassification = 'Consistently above model';
    trendClass = 'bg-amber-100 text-amber-800 border-amber-300';
  } else if (maxConsecutiveBelow >= 2 && belowCount > totalSamples / 2) {
    trendClassification = 'Consistently below model';
    trendClass = 'bg-blue-100 text-blue-800 border-blue-300';
  } else if (signFlips >= 2 || stdDev > 15) {
    if (hasExclusions) {
      // User has excluded suspect samples — remaining data may still have some variance
      // but should NOT be labelled "volatile" since the anomaly has been intentionally removed
      if (belowCount > aboveCount) {
        trendClassification = 'Below model (after sample exclusion)';
        trendClass = 'bg-blue-50 text-blue-800 border-blue-300';
      } else if (aboveCount > belowCount) {
        trendClassification = 'Above model (after sample exclusion)';
        trendClass = 'bg-amber-50 text-amber-800 border-amber-300';
      } else {
        trendClassification = 'Mixed pattern (after sample exclusion)';
        trendClass = 'bg-gray-100 text-gray-800 border-gray-300';
      }
    } else {
      // No exclusions, high variance — initial classification is volatile.
      // This may be refined later by generateIntelligence.js after jump validation
      // identifies unconfirmed jumps (likely sampling errors).
      trendClassification = 'Volatile / inconsistent';
      trendClass = 'bg-purple-100 text-purple-800 border-purple-300';
    }
  } else if (aboveCount > belowCount) {
    trendClassification = 'Trending above model';
    trendClass = 'bg-amber-50 text-amber-700 border-amber-200';
  } else if (belowCount > aboveCount) {
    trendClassification = 'Trending below model';
    trendClass = 'bg-blue-50 text-blue-700 border-blue-200';
  } else {
    trendClassification = 'Mixed pattern';
    trendClass = 'bg-gray-100 text-gray-800 border-gray-300';
  }

  // 3) Confidence Score (0-100) — computed from ACTIVE deviations only
  let confidenceScore = 0;
  let confidenceLabel = 'Low';
  let confidenceClass = 'bg-gray-100 text-gray-700';
  let confidenceDrivers = [];
  
  // Persistence factor (consecutive positive deviations)
  const persistenceFactor = Math.min(maxConsecutiveAbove * 15, 40);
  if (maxConsecutiveAbove >= 2) {
    confidenceDrivers.push(`${maxConsecutiveAbove} consecutive samples above model`);
  }
  
  // Magnitude factor (average positive deviation) — from active deviations only
  const positiveDeviations = activeDeviations.filter(d => d.deviation_percent > 5);
  const avgPositiveDeviation = positiveDeviations.length > 0 
    ? positiveDeviations.reduce((sum, d) => sum + d.deviation_percent, 0) / positiveDeviations.length 
    : 0;
  const magnitudeFactor = Math.min(avgPositiveDeviation * 2, 35);
  if (avgPositiveDeviation > 10) {
    confidenceDrivers.push(`Average deviation of +${avgPositiveDeviation.toFixed(1)}% above model`);
  }
  
  // Stability factor (lower variance = higher confidence)
  const stabilityFactor = stdDev < 10 ? 25 : stdDev < 20 ? 15 : 5;
  if (stdDev < 10) {
    confidenceDrivers.push('Consistent deviation pattern (low variance)');
  } else if (stdDev > 20) {
    confidenceDrivers.push('High variance in deviations reduces confidence');
  }
  
  confidenceScore = Math.min(Math.round(persistenceFactor + magnitudeFactor + stabilityFactor), 100);
  
  // Reduce confidence when incorrect / erroneous samples are present in ACTIVE data
  // Only penalize for suspects that remain in the analysis (not already excluded)
  const earlyIncorrectCheck = detectIncorrectSamples(activeDeviations);
  if (earlyIncorrectCheck.detected) {
    const penalty = earlyIncorrectCheck.count * 12;
    confidenceScore = Math.max(0, confidenceScore - penalty);
    confidenceDrivers.push(
      `${earlyIncorrectCheck.count} likely erroneous sample(s) detected — reduces confidence by ${penalty} pts`
    );
  }

  // Boost confidence when user has excluded suspect samples, improving data quality
  if (hasExclusions && excludedIndices.size > 0) {
    const excludedCount = excludedIndices.size;
    // Recalculate stability from cleaned dataset
    const cleanedStdDev = stdDev; // already computed from activeDeviations
    const qualityBoost = cleanedStdDev < 10 ? excludedCount * 8 : excludedCount * 5;
    confidenceScore = Math.min(100, confidenceScore + qualityBoost);
    confidenceDrivers.push(
      `Data quality improved: ${excludedCount} anomalous sample${excludedCount > 1 ? 's' : ''} excluded (+${qualityBoost} pts)`
    );
  }

  if (confidenceScore >= 70) {
    confidenceLabel = 'High';
    confidenceClass = 'bg-red-100 text-red-700 border-red-300';
  } else if (confidenceScore >= 40) {
    confidenceLabel = 'Moderate';
    confidenceClass = 'bg-amber-100 text-amber-700 border-amber-300';
  } else {
    confidenceLabel = 'Low';
    confidenceClass = 'bg-green-100 text-green-700 border-green-300';
  }

  // Phase B safeguard: Tilapia uses a STATIC time-driven growth curve that
  // cannot adapt to water-temperature swings. A cold/warm cage produces
  // false-positive or false-negative missing-fish signals, so we clamp the
  // confidence ONE step down until 50+ cycles of field validation have built
  // a bias correction. See /app/memory/TILAPIA_MODEL_INTEGRATION.md §5.
  //
  // BYPASS: When the user explicitly toggles "Guard Rails OFF" (Indicative
  // Report mode) the clamp is skipped so Tilapia behaves identically to
  // Seabass/Seabream/Tripletail under override.
  const speciesLower = (species || '').toLowerCase();
  const isTilapia = speciesLower.includes('tilapia') || speciesLower.includes('oreochromis');
  if (isTilapia && !bypassSpeciesClamp) {
    const downgradeMap = { High: 'Moderate', Moderate: 'Low', Low: 'Indication Only' };
    const classMap = {
      Moderate: 'bg-amber-100 text-amber-700 border-amber-300',
      Low: 'bg-green-100 text-green-700 border-green-300',
      'Indication Only': 'bg-slate-100 text-slate-700 border-slate-300',
    };
    if (downgradeMap[confidenceLabel]) {
      const originalLabel = confidenceLabel;
      confidenceLabel = downgradeMap[originalLabel];
      confidenceClass = classMap[confidenceLabel] || confidenceClass;
      confidenceDrivers.push(
        `Tilapia static-model safeguard: confidence clamped from "${originalLabel}" to "${confidenceLabel}" — temperature variance can bias missing-fish signal.`
      );
    }
  }

  // 5) Earliest Actionable Detection Date — use active deviations only
  let earliestActionableDate = null;
  let earliestActionableDays = null;
  
  if (trendClassification.includes('above') && positiveDeviations.length >= 1) {
    for (let i = 0; i < activeDeviations.length; i++) {
      if (activeDeviations[i].deviation_percent > 5) {
        if (i === activeDeviations.length - 1 || activeDeviations[i + 1].deviation_percent >= 0) {
          earliestActionableDate = activeDeviations[i].sample_date;
          earliestActionableDays = activeDeviations[i].days_since_arrival;
          break;
        }
      }
    }
    if (!earliestActionableDate && positiveDeviations.length > 0) {
      earliestActionableDate = positiveDeviations[0].sample_date;
      earliestActionableDays = positiveDeviations[0].days_since_arrival;
    }
  }

  // 6) Feeding Impact Classification (replaces feed waste estimate)
  //    Stock accuracy is PRIMARY signal; feeding impact is SECONDARY interpretation.
  //    Do NOT assume missing fish = feed waste.
  let feedingImpact = null;
  const hasDailyFeedData = dailyFeedData && dailyFeedData.length > 0;
  
  if (hasDailyFeedData) {
    const totalActualFeed = dailyFeedData.reduce((sum, d) => sum + (d.feed_kg || 0), 0);
    const totalRecommendedFeed = dailyFeedData.reduce((sum, d) => sum + (d.recommended_feed_kg || 0), 0);
    const rfi = totalRecommendedFeed > 0 ? Math.round((totalActualFeed / totalRecommendedFeed) * 1000) / 10 : null;
    
    let feedingState = null;
    if (rfi != null) {
      feedingState = rfi > 100 ? 'Overfeeding' : rfi >= 85 ? 'Optimal' : 'Restricted';
    }
    
    feedingImpact = {
      available: true,
      rfi,
      feedingState,
      total_actual_feed_kg: Math.round(totalActualFeed * 10) / 10,
      total_recommended_feed_kg: Math.round(totalRecommendedFeed * 10) / 10,
    };
  } else {
    feedingImpact = {
      available: false,
      message: 'Feeding impact classification unavailable (no daily feed data provided)'
    };
  }

  // ============================================================================
  // ENHANCED ANALYTICAL SIGNALS (using stockIntelligence utilities)
  // All signals use activeDeviations (excluded samples fully removed)
  // ============================================================================
  
  // Pattern classification
  const patternAnalysis = classifyDeviationPattern(activeDeviations, trendClassification);
  
  // Positive deviation streak
  const streakAnalysis = calculatePositiveStreak(activeDeviations);
  
  // Deviation stability
  const stabilityAnalysis = calculateDeviationStability(activeDeviations);
  
  // Earliest divergence detection
  const divergenceAnalysis = detectEarliestDivergence(activeDeviations);
  
  // Growth signal strength
  const signalAnalysis = calculateGrowthSignalStrength(activeDeviations, streakAnalysis, stabilityAnalysis);
  
  // Biomass-density estimate (for Growth vs Model and Spreadsheet)
  const biomassDensityEstimate = estimateBiomassDensity(activeDeviations, stabilityAnalysis, null, trendClassification);
  
  // Sample reliability evaluation — use active comparisons only
  const activeComparisons = comparisons.filter((_, idx) => !excludedIndices.has(idx));
  const reliabilityAnalysis = evaluateSampleReliability(activeComparisons, activeDeviations);
  
  // Incorrect / erroneous sample detection — on active data only
  const incorrectSampleAnalysis = detectIncorrectSamples(activeDeviations);
  
  // Spreadsheet-specific signals: Historical deviation analysis
  const strongestWindow = findStrongestDeviationWindow(activeDeviations);
  const analyticalStrength = calculateAnalyticalStrength(activeComparisons);
  const dataQualityWarnings = detectDataQualityWarnings(activeComparisons);

  return {
    deviations,
    trend: {
      classification: trendClassification,
      class: trendClass
    },
    confidence: {
      score: confidenceScore,
      label: confidenceLabel,
      class: confidenceClass,
      drivers: confidenceDrivers
    },
    explanations: [],
    earliestActionable: earliestActionableDate ? {
      date: earliestActionableDate,
      days_since_arrival: earliestActionableDays
    } : null,
    feedingImpact,
    stats: {
      totalSamples,
      aboveCount,
      belowCount,
      withinCount,
      avgPositiveDeviation: Math.round(avgPositiveDeviation * 10) / 10,
      stdDev: Math.round(stdDev * 10) / 10,
      hasExclusions,
      excludedCount: excludedIndices.size,
      totalRawSamples: comparisons.length,
    },
    // Enhanced analytical signals
    analyticalSignals: {
      pattern: {
        pattern: patternAnalysis.pattern,
        avgChange: patternAnalysis.avgChange,
        volatility: patternAnalysis.volatility,
        signChanges: patternAnalysis.signChanges,
      },
      streak: {
        maxStreak: streakAnalysis.maxStreak,
        currentStreak: streakAnalysis.currentStreak,
      },
      stability: {
        stability: stabilityAnalysis.stability,
        stdDev: stabilityAnalysis.stdDev,
        range: stabilityAnalysis.range,
      },
      divergence: divergenceAnalysis,
      signalStrength: {
        strength: signalAnalysis.strength,
        score: signalAnalysis.score,
      },
      biomassDensity: {
        available: biomassDensityEstimate.available,
        signalStrength: biomassDensityEstimate.signalStrength,
        estimatedReductionRange: biomassDensityEstimate.estimatedReductionRange,
        estimatedFishReduction: biomassDensityEstimate.estimatedFishReduction,
      },
      reliability: reliabilityAnalysis,
      incorrectSamples: {
        detected: incorrectSampleAnalysis.detected,
        count: incorrectSampleAnalysis.count,
        samples: incorrectSampleAnalysis.samples,
      },
      strongestWindow,
      analyticalStrength: {
        score: analyticalStrength.score,
        label: analyticalStrength.label,
      },
      dataQualityWarnings,
    },
  };
};
